import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import type { IModalDialogContent, ModalDialogOptions, ModalWindow } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';
import { ToggleFormButton } from '@components/ToggleFormButton.tsx';

export type SetExpirationResult = { expirationDate: string | null };

type Props = { currentExpiration: string | null };

@observer
export class SetExpirationDialog extends React.Component<Props> implements IModalDialogContent<SetExpirationResult> {
  @observable private _value: string;
  @observable private _noExpiration: boolean;

  constructor(props: Props) {
    super(props);
    makeObservable(this);
    const current = props.currentExpiration ? new Date(props.currentExpiration) : null;
    this._noExpiration = current === null;
    this._value = current ? this._toInputValue(current) : this._toInputValue(this._defaultExpiration());
  }

  private _defaultExpiration(): Date {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return d;
  }

  private _toInputValue(d: Date): string {
    return d.toISOString().slice(0, 10);
  }

  getModalOptions(window: ModalWindow<SetExpirationResult>): ModalDialogOptions<SetExpirationResult> {
    return {
      title: 'Set Expiration Date',
      width: '380px',
      closeByEscape: true,
      closeOnClickOutside: true,
      buttons: [
        {
          title: 'Save',
          type: ModalButtonType.Add,
          color: 'primary',
          onClick: () => {
            const result: SetExpirationResult = {
              expirationDate: this._noExpiration ? null : this._value,
            };
            window.setPayload(result);
            window.close(ModalButtonType.Add);
          },
        },
        {
          title: 'Cancel',
          type: ModalButtonType.Cancel,
          onClick: () => window.close(ModalButtonType.Cancel),
        },
      ],
    };
  }

  render() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <ToggleFormButton
          label="No expiration (keep forever)"
          checked={this._noExpiration}
          onChange={this._onToggleNoExpiration}
        />
        {!this._noExpiration && (
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Expiration date</label>
            <input
              type="date"
              className="form-control"
              value={this._value}
              min={new Date().toISOString().slice(0, 10)}
              onChange={this._onDateChange}
            />
          </div>
        )}
      </div>
    );
  }

  @action.bound
  private _onToggleNoExpiration(checked: boolean) {
    this._noExpiration = checked;
  }

  @action.bound
  private _onDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    this._value = e.target.value;
  }
}
